import React, {useEffect, useState} from "react";
import {Box, Button, Grid, Typography} from "@mui/material";
import {PageSpinner} from "../../../../../../com/pagesTab/pagesSpinner/spinner";
import {makeStyles} from "@mui/styles";
import {AccountSettingWithLangRequestDto, FlyerLanguageDto} from "../../../../../../../dtos";
import {useTypedSelector} from "../../../../../../../hooks/useTypeSelector";
import {SelectWithLabel} from "../../../../../../../com/selectWithLabel/selectWithLabel";
import {
    TextAreaWithSwitchControlAndLabel
} from "../../../../../../../com/textAreaWithSwitchControlAndLabel/TextAreaWithSwitchControlAndLabel";
import {TextInputWithLabel} from "../../../../../../../com/textInputWithLabel/textInputWithLabel";
import {PopupState} from "../../../../../flyerDetailsPopup/flyerDetailsRightPanel";
import {useDispatch} from "react-redux";
import {setAccountSettingAndLang} from "../../../../../../../store/actionCreators/accountSettingAndLangActionCreators";
import {ClosePopupBtn} from "../../../../../../../com/closePopupBtn/closePopupBtn";
import validator from "validator";
import {isUnicode} from "../../../../../../../globals/globFunctions";


const useStyles = makeStyles(() => ({
    localAndTextSettingWrapper: {
        width: `646px`,
        backgroundColor: `#ffffff`,
        maxHeight: `98vh`,
        display: `flex`,
        flexFlow: `column`,
    },
    localAndTextSettingHeader: {
        padding: `6px 20px`,
        background: `linear-gradient(89.41deg, #0198D2 0%, #3540A8 100%)`,
        "& .MuiButton-root": {
            "& > svg": {
                "& > path": {
                    fill: `#ffffff`,
                },
            },
        },
    },
    localAndTextSettingContent: (props: any) => ({
        padding: props.settingForAccount ? `10px 30px` : `0`
    }),
    localAndTextSettingBtnsWrapper: {
        padding: `10px 30px`,
        display: `flex`,
        justifyContent: `flex-end`,
        '& > .MuiButton-root': {
            padding: `10px 25px`,
            textTransform: `capitalize`,
            color: `#ffffff`,
            background: `linear-gradient(135.47deg, #DD5A01 3.9%, #F0CA21 100%)`,
            "&.Mui-disabled": {
                opacity: `0.5`,
                color: `#ffffff`
            },
            "& > .MuiTypography-root": {
                lineHeight: `15px`,
            },
        }
    }

}))

interface AccountLocalAndTextStateDto {
    jobCaption?: string;
    language?: {
        value?: string,
        label?: string,
        language?: FlyerLanguageDto
    };
    currency?: {
        value?: string,
        label?: string,
    };
    disclaimerTitle?: string;
    disclaimer?: string;
    showDisclaimer?: boolean;
    quickTips?: string;
    showQuickTips?: boolean;
    listDisclaimer?: string;
    showListDisclaimer?: boolean;
    checkoutBtn?: string;
    homePageUrl?: string;
}


interface Props {
    closePopup?: VoidFunction;
    settingForAccount?: boolean;
    readonly?: boolean;
    data?: PopupState;
    changeData?: (popupState: PopupState) => void;
    isNew?: boolean;
}

export const AccountLocalAndTextSetting: React.FC<Props> = ({
                                                                closePopup,
                                                                settingForAccount,
                                                                readonly,
                                                                data,
                                                                changeData,
                                                                isNew
                                                            }) => {

    const properties = {
        settingForAccount: settingForAccount
    }
    const classes = useStyles(properties);
    const dispatch = useDispatch();
    const flyersState = useTypedSelector((state) => state.flyers);
    const accountSettingWithLang = useTypedSelector((state) => state.accountSettingWithLang);
    const languages = flyersState?.languageCultures?.map((lang: FlyerLanguageDto) => {
        return {
            value: lang.code,
            label: lang.caption,
            language: {
                ...lang
            }
        }
    });
    const currencies = flyersState?.languageCultures?.map((lang: FlyerLanguageDto) => {
        return {
            value: lang.currencySymbol,
            label: lang.currencySymbol
        }
    });

    const [state, setState] = useState<AccountLocalAndTextStateDto>({
        jobCaption: ``,
        language: {
            value: "en-US",
            label: `English (US)`,
        },
        currency: {
            value: `$`,
            label: `$`
        },
        disclaimerTitle: '',
        disclaimer: ``,
        showDisclaimer: false,
        quickTips: ``,
        showQuickTips: false,
        listDisclaimer: ``,
        showListDisclaimer: false,
        checkoutBtn: ``,
        homePageUrl: ``
    });
    const [defaultState, setDefaultState] = useState<AccountLocalAndTextStateDto>({
        jobCaption: ``,
        language: {
            value: "en-US",
            label: `English (US)`,
        },
        currency: {
            value: `$`,
            label: `$`
        },
        disclaimerTitle: '',
        disclaimer: ``,
        showDisclaimer: false,
        quickTips: ``,
        showQuickTips: false,
        listDisclaimer: ``,
        showListDisclaimer: false,
        checkoutBtn: ``,
        homePageUrl: ``
    });

    const [isDefaultStateChanged, setStatusForDefaultStateChanged] = useState<boolean>(false);
    const [disclaimerDirection, setDisclaimerDirection] = useState<`rtl` | `ltr`>('ltr');
    const [disclaimerTitleDirection, setDisclaimerTitleDirection] = useState<`rtl` | `ltr`>('ltr');
    const [quickTipsDirection, setQuickTipsDirection] = useState<`rtl` | `ltr`>('ltr');
    const [itemsDisclaimerDirection, setItemsDisclaimerDirection] = useState<`rtl` | `ltr`>('ltr');
    const [isLoading, setLoadingStatus] = useState<boolean>(false);

    useEffect(() => {

        setLoadingStatus(Boolean(accountSettingWithLang.loading));
        if (accountSettingWithLang.type === "SET_ACCOUNT_SETTING_SUCCESS") {
            onClosePopup();
        }
    }, [accountSettingWithLang])

    useEffect(() => {
        let stateObjectUsingCurrentData: AccountLocalAndTextStateDto = {};
        const accountLanguage: FlyerLanguageDto | undefined = flyersState?.languageCultures?.find((lang: FlyerLanguageDto) => {
            if (lang.id === accountSettingWithLang.languageId) {
                return lang;
            }
        })
        if (settingForAccount || isNew && !data) {
            stateObjectUsingCurrentData = {
                jobCaption: ``,
                language: {
                    value: accountLanguage ? accountLanguage.code : "en-US",
                    label: accountLanguage ? accountLanguage.caption : `English (US)`,
                    language: accountLanguage ? {...accountLanguage} : {
                        caption: "English (US)",
                        captionNative: "English (US)",
                        code: "en-US",
                        currencySymbol: "$",
                        currencyAbbrv: "USD",
                        dir: "LTR",
                        id: 1
                    }
                },
                currency: {
                    value: accountLanguage ? accountLanguage.currencySymbol : "$",
                    label: accountLanguage ? accountLanguage.currencySymbol : "$"
                },
                disclaimerTitle: accountSettingWithLang.accountSetting?.uiTexts?.flyerDisclaimerPopupTitle ? accountSettingWithLang.accountSetting?.uiTexts?.flyerDisclaimerPopupTitle : '',
                disclaimer: accountSettingWithLang.accountSetting?.disclaimers?.flyerDisclaimer ? accountSettingWithLang.accountSetting?.disclaimers?.flyerDisclaimer : ``,
                showDisclaimer: Boolean(accountSettingWithLang.accountSetting?.disclaimers?.isFlyerDisclaimer),
                quickTips: accountSettingWithLang.accountSetting?.uiTexts?.quickTips ? accountSettingWithLang.accountSetting?.uiTexts?.quickTips : ``,
                showQuickTips: Boolean(accountSettingWithLang.accountSetting?.uiTexts?.isQuickTips),
                listDisclaimer: accountSettingWithLang.accountSetting?.disclaimers?.listDisclaimer ? accountSettingWithLang.accountSetting?.disclaimers?.listDisclaimer : ``,
                showListDisclaimer: Boolean(accountSettingWithLang.accountSetting?.disclaimers?.isListDisclaimer),
                checkoutBtn: accountSettingWithLang.accountSetting?.uiTexts?.goToCheckout && accountSettingWithLang.accountSetting?.uiTexts?.goToCheckout !== '' ? accountSettingWithLang.accountSetting?.uiTexts?.goToCheckout : `Go to Checkout`,
                homePageUrl: accountSettingWithLang.accountSetting?.urls && accountSettingWithLang.accountSetting?.urls.accountHomePageUrl ? accountSettingWithLang.accountSetting?.urls.accountHomePageUrl : ``
            }
        } else {
            const selectedLangData = languages?.find((lang: any) => {
                if (lang.value === data?.language.value) {
                    return lang;
                }
            });
            stateObjectUsingCurrentData = {
                jobCaption: data?.caption ? data?.caption : ``,
                language: {
                    value: data?.language.value,
                    label: data?.language.label,
                    language: selectedLangData?.language
                },
                currency: {
                    value: data?.currency.value,
                    label: data?.currency.value
                },
                disclaimerTitle: data?.uiText?.flyerDisclaimerPopupTitle ? data?.uiText?.flyerDisclaimerPopupTitle : '',
                disclaimer: data?.disclaimers?.flyerDisclaimer ? data?.disclaimers?.flyerDisclaimer : ``,
                showDisclaimer: Boolean(data?.disclaimers?.isFlyerDisclaimer),
                quickTips: data?.uiText?.quickTips ? data?.uiText?.quickTips : ``,
                showQuickTips: Boolean(data?.uiText?.isQuickTips),
                listDisclaimer: data?.disclaimers?.listDisclaimer ? data?.disclaimers?.listDisclaimer : ``,
                showListDisclaimer: Boolean(data?.disclaimers?.isListDisclaimer),
                checkoutBtn: data?.uiText?.goToCheckout ? data?.uiText?.goToCheckout : ``,
                homePageUrl: data?.urls && data?.urls.accountHomePageUrl ? data?.urls.accountHomePageUrl : ``
            }
        }
        setState(stateObjectUsingCurrentData);
        setDefaultState(stateObjectUsingCurrentData);
    }, []);

    useEffect(() => {
        setDisclaimerTitleDirection(isUnicode(state.disclaimerTitle ? state.disclaimerTitle : ``) ? `rtl` : `ltr`);
        setDisclaimerDirection(isUnicode(state.disclaimer ? state.disclaimer : ``) ? `rtl` : `ltr`);
        setQuickTipsDirection(isUnicode(state.quickTips ? state.quickTips : ``) ? `rtl` : `ltr`);
        setItemsDisclaimerDirection(isUnicode(state.listDisclaimer ? state.listDisclaimer : ``) ? `rtl` : `ltr`);
        const isStateWasChanged = checkIfSomethingChangeInDefaultState();
        if (!settingForAccount && data && changeData) {
            const newDataForFlyer: PopupState = {
                ...data,
/*
                language: {
                    value: state.language?.value ? state.language?.value : ``,
                    label: state.language?.label ? state.language?.label : ``
                },
                languageData: {
                    flyLacRec: {
                        ...data.languageData.flyLacRec,
                        lacRecId: state.language?.language?.id,
                        lacCode: state.language?.language?.code,
                        lacCaption: state.language?.language?.caption,
                        lacDir: state.language?.language?.dir,
                        lacCurrencySymbol: state.language?.language?.currencySymbol
                    },
                    flyLacRecId: state.language?.language?.id!,
                },
                currency: {
                    value: state.currency?.value ? state.currency?.value : ``,
                    label: state.currency?.value ? state.currency?.value : ``,
                },
*/
                uiText: {
                    ...data.uiText,
                    flyerDisclaimerPopupTitle: state.disclaimerTitle,
                    quickTips: state.quickTips,
                    isQuickTips: state.showQuickTips,
                    goToCheckout: state.checkoutBtn
                },
                disclaimers: {
                    ...data.disclaimers,
                    flyerDisclaimer: state.disclaimer,
                    isFlyerDisclaimer: state.showDisclaimer,
                    isListDisclaimer: state.showListDisclaimer,
                    listDisclaimer: state.listDisclaimer
                },
                urls: {
                    accountHomePageUrl: state.homePageUrl ? state.homePageUrl : ``
                }
            }
            changeData(newDataForFlyer);
        }
        setStatusForDefaultStateChanged(isStateWasChanged);

    }, [state])

    const changeCurrency = (item: any) => {
        setState({
            ...state,
            currency: {
                value: item.value,
                label: item.label
            },
        });
        console.log(`Option selected:`, item);
    };


    const changeLang = (item: any) => {
        setState({
            ...state,
            language: {
                ...item,
                value: item.value,
                label: item.label
            },
            currency: {
                value: item.language.currencySymbol,
                label: item.language.currencySymbol,
            }
        });
        console.log(`Option selected:`, item);
    };

    const onChangeText = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const targetName = event.target.name;
        const value = event.target.value;
        setState({
            ...state,
            [targetName]: value,
        });
    }

    const onChange = (checked: boolean, name: string) => {
        // const isChecked = event.target.checked;
        setState({
            ...state,
            [name]: checked,
        });

    }

    const onCopyFromAccount = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const target = e.currentTarget as HTMLButtonElement;
        const targetName = target.name;
        let value: string = '';
        switch (targetName) {
            case `disclaimer`: {
                const title = accountSettingWithLang.accountSetting?.uiTexts?.flyerDisclaimerPopupTitle ? accountSettingWithLang.accountSetting?.uiTexts?.flyerDisclaimerPopupTitle : ``;
                const text = accountSettingWithLang.accountSetting?.disclaimers?.flyerDisclaimer ? accountSettingWithLang.accountSetting?.disclaimers?.flyerDisclaimer : ``;
                setState({
                    ...state,
                    disclaimerTitle: title,
                    disclaimer: text
                })
                break;
            }
            case `quickTips`: {
                value = accountSettingWithLang.accountSetting?.uiTexts?.quickTips ? accountSettingWithLang.accountSetting?.uiTexts?.quickTips : ``;
                setState({
                    ...state,
                    [targetName]: value
                })
                break;
            }
            case `listDisclaimer`: {
                value = accountSettingWithLang.accountSetting?.disclaimers?.listDisclaimer ? accountSettingWithLang.accountSetting?.disclaimers?.listDisclaimer.slice(0, 110) : ``;
                setState({
                    ...state,
                    [targetName]: value
                })
                break;
            }
            case `homePageUrl`: {
                value = accountSettingWithLang.accountSetting?.urls && accountSettingWithLang.accountSetting?.urls.accountHomePageUrl ? accountSettingWithLang.accountSetting?.urls.accountHomePageUrl : ``;
                setState({
                    ...state,
                    [targetName]: value
                })
            }
        }
    }

    const onClosePopup = () => {
        if (settingForAccount && closePopup) {
            closePopup();
        }
    }

    const saveAccountLocalAndTextSettings = () => {
        console.log(state);
        const request: AccountSettingWithLangRequestDto = {
            languageId: state.language?.language?.id,
            disclaimers: {
                isListDisclaimer: state.showListDisclaimer,
                listDisclaimer: state.listDisclaimer,
                isFlyerDisclaimer: state.showDisclaimer,
                flyerDisclaimer: state.disclaimer,
                isEnableShowFlyerDisclaimerOnce: accountSettingWithLang.accountSetting?.disclaimers?.isEnableShowFlyerDisclaimerOnce
            },
            uiTexts: {
                goToCheckout: state.checkoutBtn,
                flyerDisclaimerPopupTitle: state.disclaimerTitle,
                isQuickTips: state.showQuickTips,
                quickTips: state.quickTips
            },
            urls: {
                accountHomePageUrl: state.homePageUrl
            }
        }
        dispatch(setAccountSettingAndLang(request));
    }

    const checkIfSomethingChangeInDefaultState = () => {
        const stringDefaultState = JSON.stringify(defaultState);
        const stringCurrentState = JSON.stringify(state);
        return stringDefaultState !== stringCurrentState;
    }

    return (
        <>
            {
                settingForAccount &&
                <>
                    {
                        isLoading ? <Box
                                sx={{
                                    height: `100vh`,
                                    width: `100vw`,
                                    display: `flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                }}
                            >
                                <PageSpinner/>
                            </Box> :
                            <Box className={classes.localAndTextSettingWrapper}>
                                {/*header*/}
                                {
                                    settingForAccount &&
                                    <Box
                                        className={classes.localAndTextSettingHeader}
                                    >
                                        <Grid container alignItems={`center`}>
                                            <Grid item xs={10}>
                                                <Typography color={`#ffffff`} fontWeight={700}>
                                                    Account Startup Settings
                                                </Typography>
                                            </Grid>
                                            <Grid item xs>
                                                <Box sx={{display: `flex`, justifyContent: `flex-end`}}>
                                                    <ClosePopupBtn onClose={onClosePopup}/>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                }
                                {/*content*/}
                                <Box className={classes.localAndTextSettingContent}
                                     sx={{
                                         flex: `1 1 auto`,
                                         overflow: "auto"
                                     }}
                                >
                                    {/*rows*/}
                                    <Grid container alignItems={`center`} mb={`30px`}>
                                        <SelectWithLabel
                                            labelGridSizeNumber={4}
                                            selectGridSizeNumber={3}
                                            valueForSelect={state.language}
                                            onChangeSelect={changeLang}
                                            selectArray={languages}
                                            labelCaption={'language:'}
                                            isBold={true}
                                            isRequired={true}
                                            readonly={readonly ? readonly : false}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`center`} mb={`30px`}>
                                        <SelectWithLabel
                                            labelGridSizeNumber={4}
                                            selectGridSizeNumber={3}
                                            valueForSelect={state.currency}
                                            onChangeSelect={changeCurrency}
                                            selectArray={currencies}
                                            labelCaption={'currency:'}
                                            isBold={true}
                                            isRequired={true}
                                            readonly={true}
                                            // readonly={readonly ? readonly : false}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`10px`}>
                                        <TextAreaWithSwitchControlAndLabel
                                            labelGridSizeNumber={4}
                                            // valueForTextarea={state.disclaimer}
                                            // onChangeTextarea={onChangeText}
                                            onChange={onChange}
                                            labelCaption={`Opening Popup:`}
                                            isChecked={state.showDisclaimer}
                                            isBold={true}
                                            fieldName={`disclaimer`}
                                            checkboxName={`showDisclaimer`}
                                            readonly={readonly ? readonly : false}
                                            // direction={disclaimerDirection}
                                            isShowFromAccountBtn={settingForAccount}
                                            onCopyFromAccount={onCopyFromAccount}
                                            hideTextArea={true}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`10px`}>
                                        <TextInputWithLabel
                                            labelGridSizeNumber={4}
                                            valueForInput={state.disclaimerTitle}
                                            onChangeInput={onChangeText}
                                            labelCaption={`Title:`}
                                            isBold={true}
                                            fieldName={`disclaimerTitle`}
                                            maxLength={1000}
                                            readonly={readonly ? readonly : false}
                                            rows={2}
                                            direction={disclaimerTitleDirection}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`30px`}>
                                        <TextInputWithLabel
                                            labelGridSizeNumber={4}
                                            valueForInput={state.disclaimer}
                                            onChangeInput={onChangeText}
                                            labelCaption={`Text:`}
                                            isBold={true}
                                            fieldName={`disclaimer`}
                                            direction={disclaimerDirection}
                                            readonly={readonly ? readonly : false}
                                            maxLength={2000}
                                            rows={3}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`30px`}>
                                        <TextAreaWithSwitchControlAndLabel
                                            labelGridSizeNumber={4}
                                            valueForTextarea={state.quickTips}
                                            onChangeTextarea={onChangeText}
                                            onChange={onChange}
                                            labelCaption={`Quick Tips:`}
                                            isBold={true}
                                            isChecked={state.showQuickTips}
                                            fieldName={`quickTips`}
                                            checkboxName={`showQuickTips`}
                                            readonly={readonly ? readonly : false}
                                            direction={quickTipsDirection}
                                            isShowFromAccountBtn={settingForAccount}
                                            onCopyFromAccount={onCopyFromAccount}
                                            numberOfRows={3}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`30px`}>
                                        <TextAreaWithSwitchControlAndLabel
                                            labelGridSizeNumber={4}
                                            valueForTextarea={state.listDisclaimer}
                                            onChangeTextarea={onChangeText}
                                            onChange={onChange}
                                            labelCaption={`Items Disclaimer:`}
                                            isBold={true}
                                            numberOfRows={2}
                                            isChecked={state.showListDisclaimer}
                                            fieldName={`listDisclaimer`}
                                            checkboxName={`showListDisclaimer`}
                                            readonly={readonly ? readonly : false}
                                            direction={itemsDisclaimerDirection}
                                            isShowFromAccountBtn={settingForAccount}
                                            onCopyFromAccount={onCopyFromAccount}
                                            maxLength={110}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`flex-start`} mb={`30px`}>
                                        <TextAreaWithSwitchControlAndLabel
                                            labelGridSizeNumber={4}
                                            valueForTextarea={state.homePageUrl}
                                            onChangeTextarea={onChangeText}
                                            labelCaption={`Home Page URL:`}
                                            isBold={true}
                                            fieldName={`homePageUrl`}
                                            readonly={readonly ? readonly : false}
                                            isShowFromAccountBtn={settingForAccount}
                                            onCopyFromAccount={onCopyFromAccount}
                                            maxLength={110}
                                            isNotValid={!validator.isURL(state.homePageUrl ? state.homePageUrl : ``)}
                                            isRequired={true}
                                        />
                                    </Grid>
                                    <Grid container alignItems={`center`} mb={`30px`}>
                                        <TextInputWithLabel
                                            labelGridSizeNumber={4}
                                            valueForInput={state.checkoutBtn}
                                            onChangeInput={onChangeText}
                                            labelCaption={`Checkout Button Text:`}
                                            isBold={true}
                                            fieldName={`checkoutBtn`}
                                            maxLength={30}
                                            readonly={readonly ? readonly : false}
                                            isRequired={true}
                                            isNotValid={state.checkoutBtn === ''}
                                        />
                                    </Grid>
                                </Box>
                                {/*buttons*/}
                                <Box className={classes.localAndTextSettingBtnsWrapper}>
                                    <Button
                                        disabled={!isDefaultStateChanged || state.checkoutBtn === '' || !validator.isURL(state.homePageUrl ? state.homePageUrl : ``)}
                                        onClick={saveAccountLocalAndTextSettings}
                                    >
                                        <Typography>
                                            {readonly ? `Edit` : `Save`}
                                        </Typography>
                                    </Button>
                                </Box>
                            </Box>
                    }
                </>
            }
            {
                !settingForAccount &&
                <Box className={classes.localAndTextSettingContent} sx={{
                    flex: `1 1 auto`,
                    overflow: "auto"
                }}>
                    {/*rows*/}
                    <Grid container alignItems={`center`} mb={`16px`} mt={`8px`}>
                        <TextInputWithLabel
                            labelGridSizeNumber={4}
                            valueForInput={state.jobCaption}
                            onChangeInput={() => {
                            }}
                            labelCaption={`Job Caption (URL):`}
                            isBold={true}
                            fieldName={`checkoutBtn`}
                            readonly={true}
                            withoutPadding={true}
                        />
                    </Grid>
                    {/*<Grid container alignItems={`center`} mb={`30px`}>*/}
                    {/*    <SelectWithLabel*/}
                    {/*        labelGridSizeNumber={4}*/}
                    {/*        selectGridSizeNumber={3}*/}
                    {/*        valueForSelect={state.language}*/}
                    {/*        onChangeSelect={changeLang}*/}
                    {/*        selectArray={languages}*/}
                    {/*        labelCaption={'language:'}*/}
                    {/*        isBold={true}*/}
                    {/*        isRequired={true}*/}
                    {/*        readonly={readonly ? readonly : false}*/}
                    {/*    />*/}
                    {/*    <SelectWithLabel*/}
                    {/*        labelGridSizeNumber={2}*/}
                    {/*        selectGridSizeNumber={3}*/}
                    {/*        valueForSelect={state.currency}*/}
                    {/*        onChangeSelect={changeCurrency}*/}
                    {/*        selectArray={currencies}*/}
                    {/*        labelCaption={'currency:'}*/}
                    {/*        labelAlign={`end`}*/}
                    {/*        isBold={true}*/}
                    {/*        isRequired={true}*/}
                    {/*        readonly={true}*/}
                    {/*        // readonly={readonly ? readonly : false}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    <Grid container alignItems={`flex-start`} mb={`10px`}>
                        <TextAreaWithSwitchControlAndLabel
                            labelGridSizeNumber={4}
                            // valueForTextarea={state.disclaimer}
                            // onChangeTextarea={onChangeText}
                            onChange={onChange}
                            labelCaption={`Opening Popup:`}
                            isChecked={state.showDisclaimer}
                            isBold={true}
                            fieldName={`disclaimer`}
                            checkboxName={`showDisclaimer`}
                            readonly={readonly ? readonly : false}
                            // direction={disclaimerDirection}
                            isShowFromAccountBtn={settingForAccount}
                            onCopyFromAccount={onCopyFromAccount}
                            hideTextArea={true}
                        />
                    </Grid>
                    <Grid container alignItems={`flex-start`} mb={`10px`}>
                        <TextInputWithLabel
                            labelGridSizeNumber={4}
                            valueForInput={state.disclaimerTitle}
                            onChangeInput={onChangeText}
                            labelCaption={`Title:`}
                            isBold={true}
                            fieldName={`disclaimerTitle`}
                            maxLength={1000}
                            readonly={readonly ? readonly : false}
                            rows={2}
                            direction={disclaimerTitleDirection}
                        />
                    </Grid>
                    <Grid container alignItems={`flex-start`} mb={`24px`}>
                        <TextInputWithLabel
                            labelGridSizeNumber={4}
                            valueForInput={state.disclaimer}
                            onChangeInput={onChangeText}
                            labelCaption={`Text:`}
                            isBold={true}
                            fieldName={`disclaimer`}
                            direction={disclaimerDirection}
                            readonly={readonly ? readonly : false}
                            maxLength={2000}
                            rows={5}
                        />
                    </Grid>
                    <Grid container alignItems={`flex-start`} mb={`24px`}>
                        <TextAreaWithSwitchControlAndLabel
                            labelGridSizeNumber={4}
                            valueForTextarea={state.quickTips}
                            onChangeTextarea={onChangeText}
                            onChange={onChange}
                            labelCaption={`Quick Tips:`}
                            isBold={true}
                            isChecked={state.showQuickTips}
                            fieldName={`quickTips`}
                            checkboxName={`showQuickTips`}
                            readonly={readonly ? readonly : false}
                            direction={quickTipsDirection}
                            isShowFromAccountBtn={settingForAccount}
                            onCopyFromAccount={onCopyFromAccount}
                            numberOfRows={5}
                        />
                    </Grid>
                    <Grid container alignItems={`flex-start`} mb={`24px`}>
                        <TextAreaWithSwitchControlAndLabel
                            labelGridSizeNumber={4}
                            valueForTextarea={state.listDisclaimer}
                            onChangeTextarea={onChangeText}
                            onChange={onChange}
                            labelCaption={`Items Disclaimer:`}
                            numberOfRows={5}
                            isBold={true}
                            isChecked={state.showListDisclaimer}
                            fieldName={`listDisclaimer`}
                            checkboxName={`showListDisclaimer`}
                            readonly={readonly ? readonly : false}
                            direction={itemsDisclaimerDirection}
                            isShowFromAccountBtn={settingForAccount}
                            onCopyFromAccount={onCopyFromAccount}
                            maxLength={110}
                        />
                    </Grid>
                    <Grid container alignItems={`flex-start`} mb={`24px`}>
                        <TextAreaWithSwitchControlAndLabel
                            labelGridSizeNumber={4}
                            valueForTextarea={state.homePageUrl}
                            onChangeTextarea={onChangeText}
                            labelCaption={`Home Page URL:`}
                            isBold={true}
                            fieldName={`homePageUrl`}
                            readonly={readonly ? readonly : false}
                            isShowFromAccountBtn={settingForAccount}
                            onCopyFromAccount={onCopyFromAccount}
                            maxLength={110}
                            isNotValid={!validator.isURL(state.homePageUrl ? state.homePageUrl : ``)}
                            isRequired={true}
                        />
                    </Grid>
                    {/*<Grid container alignItems={`center`}>*/}
                    {/*    <TextInputWithLabel*/}
                    {/*        labelGridSizeNumber={4}*/}
                    {/*        valueForInput={state.checkoutBtn}*/}
                    {/*        onChangeInput={onChangeText}*/}
                    {/*        labelCaption={`Checkout Button Text:`}*/}
                    {/*        isBold={true}*/}
                    {/*        fieldName={`checkoutBtn`}*/}
                    {/*        maxLength={30}*/}
                    {/*        readonly={readonly ? readonly : false}*/}
                    {/*        isRequired={true}*/}
                    {/*        isNotValid={errorCheckoutBtn ? errorCheckoutBtn : false}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                </Box>
            }
        </>
    )

}